import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Paginated, ProductOption, StockItem, StockMovement } from '@kontor/shared';
import { api } from '../../api';
import { ProductPicker } from '../offers/ProductPicker';
import { LocationSelect, errorText, milliText, parseMilli } from './shared';

export type BookingMode = 'receipt' | 'issue' | 'transfer' | 'adjustment';

export const BOOKING_LABELS: Record<BookingMode, string> = { receipt: 'Wareneingang', issue: 'Warenausgang', transfer: 'Umlagerung', adjustment: 'Korrektur' };

/**
 * Buchung von Hand (KON-166): Wareneingang, Warenausgang, Umlagerung oder Korrektur. Bei Waren mit Chargenführung verlangt der Wareneingang eine Chargennummer, der
 * Warenausgang nimmt von selbst zuerst die Charge mit dem frühesten Ablauf. Eine Korrektur trägt die Änderung mit Vorzeichen („-2“ für zwei weniger) und braucht einen Grund.
 * Der Wareneingang nimmt jede Ware aus den Produkten an und schaltet die Lagerführung beim ersten Eingang selbst ein, die anderen Buchungen gibt es nur für Artikel im Lager.
 */
export function BookingPanel({ mode, productId, onClose, onDone }: { mode: BookingMode; productId?: string; onClose: () => void; onDone: (moved: StockMovement[]) => void }) {
  const client = useQueryClient();
  const products = useQuery({
    queryKey: ['inventory-stock-options'],
    queryFn: async () => (await api<Paginated<StockItem>>('GET', '/inventory/stock?perPage=100&sort=name')).data,
    staleTime: 30_000,
  });
  const [product, setProduct] = useState(productId ?? '');
  const [picked, setPicked] = useState<ProductOption | null>(null);
  const [newBatch, setNewBatch] = useState(false);
  const [location, setLocation] = useState('');
  const [target, setTarget] = useState('');
  const [quantity, setQuantity] = useState('');
  const [batchNumber, setBatchNumber] = useState('');
  const [bestBefore, setBestBefore] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const item = products.data?.find((p) => p.productId === product);
  // Wareneingang: jede Ware aus den Produkten, auch eine, die noch nicht im Lager geführt wird
  const anyProduct = mode === 'receipt' && !productId;
  const isNew = anyProduct && !!product && products.isSuccess && !item;
  const batchOn = item ? item.batchTracking : isNew && newBatch;
  const unit = item?.unitSymbol ?? (isNew ? (picked?.unitSymbol ?? '') : '');
  const save = useMutation({
    mutationFn: async () => {
      const q = parseMilli(quantity);
      if (!product) throw new Error('Bitte wählen Sie einen Artikel.');
      if (!location) throw new Error(mode === 'transfer' ? 'Bitte wählen Sie den Lagerort, aus dem umgelagert wird.' : 'Bitte wählen Sie einen Lagerort.');
      if (q === null || q === 0) throw new Error('Bitte geben Sie eine Menge ein, zum Beispiel 2,5.');
      if (mode === 'transfer') {
        if (!target) throw new Error('Bitte wählen Sie den Lagerort, in den umgelagert wird.');
        return (await api<{ data: StockMovement[] }>('POST', '/inventory/transfers', { productId: product, fromLocationId: location, toLocationId: target, quantityMilli: q, note: note.trim() || null })).data;
      }
      if (isNew) await api('PUT', `/inventory/stock/${product}/settings`, { tracked: true, minStockMilli: 0, batchTracking: newBatch });
      return (
        await api<{ data: StockMovement[] }>('POST', '/inventory/movements', {
          kind: mode,
          productId: product,
          locationId: location,
          quantityMilli: q,
          batchNumber: mode === 'receipt' && batchOn ? batchNumber.trim() || null : null,
          bestBefore: mode === 'receipt' && batchOn && bestBefore ? bestBefore : null,
          note: note.trim() || null,
        })
      ).data;
    },
    onSuccess: (moved) => {
      for (const key of ['inventory-stock', 'inventory-stock-options', 'inventory-movements', 'inventory-overview', 'inventory-detail']) client.invalidateQueries({ queryKey: [key] });
      onDone(moved);
    },
    onError: (e) => setError(e instanceof Error && !('status' in e) ? e.message : errorText(e, 'Die Buchung hat nicht geklappt.')),
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    save.mutate();
  };

  return (
    <form className="card inv-booking" onSubmit={submit} aria-label={BOOKING_LABELS[mode]}>
      <h2>{BOOKING_LABELS[mode]}</h2>
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      {products.data?.length === 0 && !productId && !anyProduct && (
        <div className="alert" role="status">
          Noch kein Artikel im Lager. Buchen Sie zuerst einen Wareneingang, dabei kommt die Ware aus Ihren Produkten ins Lager.
        </div>
      )}
      <div className="form-grid">
        {anyProduct ? (
          <div className="field field--wide">
            <span className="field__label">Artikel (Ware aus Ihren Produkten)</span>
            {product ? (
              <p className="inv-picked">
                <strong>{picked?.name ?? item?.name}</strong>{(picked?.sku ?? item?.sku) ? ` (${picked?.sku ?? item?.sku})` : ''}
                {' '}<button type="button" className="link-button" onClick={() => { setProduct(''); setPicked(null); setNewBatch(false); }}>ändern</button>
              </p>
            ) : (
              <ProductPicker purpose="stock" onPick={(p) => { setProduct(p.id); setPicked(p); }} />
            )}
            {isNew && <small className="muted">Dieser Artikel wird mit dem ersten Wareneingang ins Lager aufgenommen. Gibt es ihn noch nicht, <Link to="/produkte/neu">legen Sie das Produkt an</Link>.</small>}
          </div>
        ) : (
          <label className="field field--wide">
            <span>Artikel</span>
            <select className="input" value={product} onChange={(e) => setProduct(e.target.value)} disabled={!!productId}>
              <option value="">Bitte wählen</option>
              {products.data?.map((p) => <option key={p.productId} value={p.productId}>{p.name}{p.sku ? ` (${p.sku})` : ''}</option>)}
            </select>
          </label>
        )}
        <LocationSelect value={location} onChange={setLocation} label={mode === 'transfer' ? 'Von Lagerort' : 'Lagerort'} required />
        {mode === 'transfer' && <LocationSelect value={target} onChange={setTarget} label="Nach Lagerort" required />}
        <label className="field">
          <span>{mode === 'adjustment' ? `Änderung der Menge${unit ? ` (${unit})` : ''}` : `Menge${unit ? ` (${unit})` : ''}`}</span>
          <input className="input" inputMode="decimal" value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder={mode === 'adjustment' ? '-2 oder 3,5' : '2,5'} required />
          {mode === 'adjustment' && item && <small className="muted">Bestand insgesamt: {milliText(item.quantityMilli)} {unit}</small>}
        </label>
        {isNew && (
          <label className="field field--wide inv-check">
            <span><input type="checkbox" checked={newBatch} onChange={(e) => setNewBatch(e.target.checked)} /> In Chargen führen (Chargennummer und Mindesthaltbarkeit, zum Beispiel bei Wein oder Lebensmitteln)</span>
          </label>
        )}
        {mode === 'receipt' && batchOn && (
          <>
            <label className="field">
              <span>Chargennummer</span>
              <input className="input" value={batchNumber} onChange={(e) => setBatchNumber(e.target.value)} maxLength={50} required />
            </label>
            <label className="field">
              <span>Mindestens haltbar bis (optional)</span>
              <input className="input" type="date" value={bestBefore} onChange={(e) => setBestBefore(e.target.value)} />
            </label>
          </>
        )}
        {mode === 'issue' && item?.batchTracking && <p className="muted small field--wide">Die Charge mit dem frühesten Ablauf wird zuerst entnommen.</p>}
        <label className="field field--wide">
          <span>{mode === 'adjustment' ? 'Grund der Korrektur' : 'Notiz (optional)'}</span>
          <input className="input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} required={mode === 'adjustment'} />
        </label>
      </div>
      <div className="form-actions">
        <button type="submit" className="btn btn--primary" disabled={save.isPending}>{save.isPending ? 'Wird gebucht …' : 'Buchen'}</button>
        <button type="button" className="btn btn--secondary" onClick={onClose}>Abbrechen</button>
      </div>
    </form>
  );
}
